import { EidProvider, Language } from '@/types';
import { client } from '@/client';
import { CodeRequest } from '@/auth';

export type InboxLoginRequest = {
  method: 'BANKID_SE' | 'FREJA' | 'BANKID_NO' | 'MITID' | 'FTN';
};

export type InboxLoginStatusResponse = {
  id: string;
} & (
  | {
      status: 'IN_PROGRESS';
      url?: string;
      bankId?: {
        autoStartToken: string;
        qrData: string;
      };
      freja?: {
        qrData: string;
      };
    }
  | {
      status: 'COMPLETED';
      token: string;
      user: {
        displayName: string;
        email: string | null;
        hasValidatedEmail: boolean;
      };
    }
  | {
      status: 'FAILED';
      error: string;
    }
);

export type InboxMessage = {
  id: string;
  companyName: string;
  subject: string;
  body?: string;
  footer?: string;
  datetime: number; // UNIX timestamp (seconds)
  wasRead: boolean;
  companyLogoUrl: string;
  autodeleteDate: string; // yyyy-MM-dd
  requestSignature: boolean;
  canReply: boolean;
  signature?: string;
  attachments: {
    name: string;
  }[];
  isTeleSign: boolean;
};

export type InboxSigners = {
  messageSigned: boolean;
  attachmentHash: string;
  zipCreated: boolean;
  signers: {
    signatureDate?: number;
    ssn: string;
    displayName?: string;
    email: string;
  }[];
  isTeleSign: boolean;
};

export type InboxSignResponse =
  | {
      status: 'IN_PROGRESS';
      id: string;
      url?: string;
      bankId?: {
        autoStartToken: string;
        qrData: string;
      };
    }
  | {
      status: 'COMPLETED';
    }
  | {
      status: 'FAILED';
      ref: string;
      error: string;
    };

export type ReplyRequest = {
  body: string;
};

export type InboxBrandResponse = {
  name: string | null;
  companyName: string | null;
  logoUrl: string | null;
  backgroundImage: string | null;
  primaryAccentColor: string | null;
  secondaryAccentColor: string | null;
  font: string | null;
  defaultLanguage: string | null;
  providers: EidProvider[];
  languages: Language[];
};

export async function initInboxLogin(
  request: InboxLoginRequest,
  signal?: AbortSignal,
): Promise<Extract<InboxLoginStatusResponse, { status: 'IN_PROGRESS' }>> {
  return client
    .post('/inbox/login', request, { signal })
    .then((response) => response.data);
}

export async function getInboxLoginStatus(
  id: string,
  signal?: AbortSignal,
): Promise<InboxLoginStatusResponse> {
  return client
    .get(`/inbox/login/${encodeURIComponent(id)}`, { signal })
    .then((response) => response.data);
}

export async function completeInboxLogin(
  id: string,
  request: CodeRequest,
  signal?: AbortSignal,
): Promise<Extract<InboxLoginStatusResponse, { status: 'COMPLETED' }>> {
  return client
    .post(`/inbox/login/${encodeURIComponent(id)}`, request, { signal })
    .then((response) => response.data);
}

export async function deleteInboxMessage(
  messageId: string,
  signal?: AbortSignal,
): Promise<void> {
  return client
    .delete(`/inbox/message/${messageId}`, { signal })
    .then((response) => response.data);
}

export async function forwardInboxMessage(
  messageId: string,
  signal?: AbortSignal,
): Promise<void> {
  return client
    .post(`/inbox/message/${messageId}/forward`, null, { signal })
    .then((response) => response.data);
}

export async function getInboxMessages(
  brand?: string,
  signal?: AbortSignal,
): Promise<InboxMessage[]> {
  return client
    .get<InboxMessage[]>('/inbox/message', {
      params: { brand },
      signal,
    })
    .then((response) => response.data);
}

export async function getInboxMessageSigners(
  messageId: string,
  signal?: AbortSignal,
): Promise<InboxSigners[]> {
  return client
    .get<InboxSigners[]>(`/inbox/message/${messageId}/signers`, { signal })
    .then((response) => response.data);
}

export async function markInboxMessageAsRead(
  messageId: string,
  signal?: AbortSignal,
): Promise<void> {
  return client
    .post(`/inbox/message/${messageId}/read`, null, { signal })
    .then((response) => response.data);
}

export async function signInboxMessage(
  messageId: string,
  signal?: AbortSignal,
): Promise<Extract<InboxSignResponse, { status: 'IN_PROGRESS' }>> {
  return client
    .post(`/inbox/message/${messageId}/sign`, null, { signal })
    .then((response) => response.data);
}

export async function getSignInboxMessageResult(
  messageId: string,
  ref: string,
  signal?: AbortSignal,
): Promise<InboxSignResponse> {
  return client
    .get(`/inbox/message/${messageId}/sign/${ref}`, { signal })
    .then((response) => response.data);
}

export async function getInboxAttachment(
  messageId: string,
  attachmentName: string,
  signal?: AbortSignal,
): Promise<Blob> {
  return client
    .get<Blob>(
      `/attachment/enduser/${messageId}/attachment/${attachmentName}`,
      {
        responseType: 'blob',
        signal,
      },
    )
    .then((response) => response.data);
}

export async function getInboxSignature(
  messageId: string,
  signal?: AbortSignal,
): Promise<Blob> {
  return client
    .get<Blob>(`/attachment/enduser/${messageId}/signature`, {
      responseType: 'blob',
      signal,
    })
    .then((response) => response.data);
}

export async function downloadInboxTeleSignPdf(
  messageId: string,
  signal?: AbortSignal,
): Promise<Blob> {
  return client
    .get<Blob>(`/inbox/message/${messageId}/pdf`, {
      responseType: 'blob',
      signal,
    })
    .then((response) => response.data);
}

export async function replyToInboxMessage(
  messageId: string,
  request: ReplyRequest,
  signal?: AbortSignal,
): Promise<void> {
  return client
    .post(`/inbox/message/${messageId}/reply`, request, { signal })
    .then((response) => response.data);
}

export async function getInboxBrand(
  signal?: AbortSignal,
): Promise<InboxBrandResponse> {
  return client
    .get<InboxBrandResponse>('/inbox/brand', {
      signal,
      headers: { 'X-Origin-URL': window.location.href },
    })
    .then((response) => response.data);
}
